/* Admin "view as" (issue 2902) · browser side of the READ-ONLY owner QA view.
 *
 * An owner opens another user's Wedge in a NEW tab. The server enforces
 * read-only (every non-GET is a 403, see docs/security/2026-10-19-admin-view-as-
 * threat-model.md); this module makes the tab SAFE TO LOOK AT:
 *
 *  - The access token arrives in the URL FRAGMENT (#rvva=<token>), which is never
 *    sent to a server or written to a log. It is moved into sessionStorage (one
 *    tab only) and the fragment is removed from the address bar at once.
 *  - The tab uses that token as a Bearer and NO cookie. The owner's own Wedge
 *    session may sit in another tab of the same host, so every same-origin fetch is
 *    forced to credentials:'omit' with the view-as Bearer. A request can never
 *    fall back to the owner's cookie and show the owner's data as "the user".
 *  - window.localStorage is swapped for a tab-private store BEFORE any app code
 *    runs. The owner's own keys (rv_wedge_at, rv_wedge_rt, rv_auth_user_cache,
 *    wedge_* caches) are neither read, written nor cleared, and nothing the
 *    viewed user's data writes can leak into the owner's own session.
 *  - Analytics and beacons are off; 'storage' events from the owner's tab are
 *    swallowed so a sign-out there cannot end this view or the reverse.
 *  - Fail closed: if the isolation cannot be installed the token is NOT kept and
 *    the shell shows an error instead of the app.
 */

export const VIEW_AS_HANDOFF_KEY = 'rvva';
export const VIEW_AS_TOKEN_KEY = 'rv_viewas_at';
export const VIEW_AS_ENDED_EVENT = 'rv:viewas-ended';
import { apiUrl } from './routes';

const STORE_PREFIX = 'rvva:ls:';
const TOKEN_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
/* The one write a view-as tab may make: an audit-only "I returned" call. */
const END_PATH_RE = /\/wedge\/view-as\/end\/?$/i;

export type ViewAsState = 'off' | 'active' | 'failed';

interface ViewAsWindow extends Window {
  __WEDGE_VIEW_AS__?: { state: ViewAsState; ended?: boolean };
}

function sessionStore(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** The view-as token for THIS tab, or null. Shape-checked so junk is never sent. */
export function viewAsToken(): string | null {
  try {
    const t = sessionStore()?.getItem(VIEW_AS_TOKEN_KEY) ?? null;
    return t && TOKEN_RE.test(t) ? t : null;
  } catch {
    return null;
  }
}

/** True when this tab is a view-as tab (a token is held and the session has not ended). */
export function isViewAsActive(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as ViewAsWindow;
  if (w.__WEDGE_VIEW_AS__?.ended) return false;
  return w.__WEDGE_VIEW_AS__?.state === 'active' && viewAsToken() !== null;
}

export function viewAsInstallState(): ViewAsState {
  if (typeof window === 'undefined') return 'off';
  return (window as ViewAsWindow).__WEDGE_VIEW_AS__?.state ?? 'off';
}

export function viewAsHasEnded(): boolean {
  return typeof window !== 'undefined' && Boolean((window as ViewAsWindow).__WEDGE_VIEW_AS__?.ended);
}

/** Pull the handoff token out of a location hash such as "#rvva=<jwt>". Null when absent or malformed. Pure. */
export function parseViewAsHandoff(hash: string | null | undefined): string | null {
  const h = String(hash ?? '').replace(/^#/, '');
  if (!h) return null;
  for (const part of h.split('&')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq) === VIEW_AS_HANDOFF_KEY) {
      const v = part.slice(eq + 1);
      return TOKEN_RE.test(v) ? v : null;
    }
  }
  return null;
}

/** A Storage-shaped store private to this tab: reads and writes go to sessionStorage under a prefix (or to memory when
 *  sessionStorage is unavailable). It starts empty, so none of the owner's localStorage is visible. */
export function createIsolatedStorage(backing: Storage | null): Storage {
  const mem = new Map<string, string>();
  const keys = (): string[] => {
    if (!backing) return [...mem.keys()];
    const out: string[] = [];
    for (let i = 0; i < backing.length; i++) {
      const k = backing.key(i);
      if (k && k.startsWith(STORE_PREFIX)) out.push(k.slice(STORE_PREFIX.length));
    }
    return out;
  };
  const store = {
    get length() {
      return keys().length;
    },
    key(i: number) {
      return keys()[i] ?? null;
    },
    getItem(k: string) {
      const key = String(k);
      if (!backing) return mem.has(key) ? (mem.get(key) as string) : null;
      try {
        return backing.getItem(STORE_PREFIX + key);
      } catch {
        return null;
      }
    },
    setItem(k: string, v: string) {
      const key = String(k);
      const val = String(v);
      if (!backing) {
        mem.set(key, val);
        return;
      }
      try {
        backing.setItem(STORE_PREFIX + key, val);
      } catch {
        mem.set(key, val);
      }
    },
    removeItem(k: string) {
      const key = String(k);
      mem.delete(key);
      try {
        backing?.removeItem(STORE_PREFIX + key);
      } catch {
        /* noop */
      }
    },
    clear() {
      mem.clear();
      for (const k of keys()) {
        try {
          backing?.removeItem(STORE_PREFIX + k);
        } catch {
          /* noop */
        }
      }
    },
  };
  return store as unknown as Storage;
}

function requestFacts(input: RequestInfo | URL, init?: RequestInit): { url: URL; method: string } {
  const href = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
  const url = new URL(href, window.location.href);
  const method = String(init?.method ?? (typeof input === 'object' && 'method' in input ? (input as Request).method : 'GET')).toUpperCase();
  return { url, method };
}

function blockedResponse(): Response {
  return new Response(JSON.stringify({ statusCode: 403, message: 'View-only session: changes are not allowed.', code: 'VIEW_AS_READ_ONLY' }), {
    status: 403,
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Mark the view as over: drop the token and the tab-private store, tell the shell. Idempotent. */
export function markViewAsEnded(): void {
  if (typeof window === 'undefined') return;
  const w = window as ViewAsWindow;
  if (w.__WEDGE_VIEW_AS__?.ended) return;
  try {
    sessionStore()?.removeItem(VIEW_AS_TOKEN_KEY);
  } catch {
    /* noop */
  }
  if (w.__WEDGE_VIEW_AS__?.state === 'active') {
    try {
      window.localStorage.clear(); // the isolated store only: localStorage is the shim while a view-as tab is open
    } catch {
      /* noop */
    }
  }
  w.__WEDGE_VIEW_AS__ = { state: w.__WEDGE_VIEW_AS__?.state ?? 'active', ended: true };
  try {
    window.dispatchEvent(new Event(VIEW_AS_ENDED_EVENT));
  } catch {
    /* noop */
  }
}

/** Return to the owner's own account: tell the server (audit only, best effort), end the view, close the tab. */
export async function exitViewAs(): Promise<void> {
  const token = viewAsToken();
  if (token) {
    try {
      await fetch(apiUrl('/api/wedge/view-as/end'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', keepalive: true });
    } catch {
      /* the audit row is best effort; the view still ends */
    }
  }
  markViewAsEnded();
  try {
    window.close();
  } catch {
    /* the shell shows the "you can close this tab" screen */
  }
}

/* Undo steps for the test hook only; production never calls them. */
const undo: Array<() => void> = [];

function installFetchGuard(): void {
  const originalFetch = window.fetch;
  undo.push(() => {
    window.fetch = originalFetch;
  });
  const realFetch = window.fetch.bind(window);
  /* "Ours" = this page's origin and the API origin when the backend is on another host. */
  const ownOrigins = new Set<string>([window.location.origin]);
  try {
    ownOrigins.add(new URL(apiUrl('/api'), window.location.href).origin);
  } catch {
    /* same-origin only */
  }
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    let facts: { url: URL; method: string };
    try {
      facts = requestFacts(input, init);
    } catch {
      return realFetch(input, init);
    }
    if (!ownOrigins.has(facts.url.origin)) return realFetch(input, init);
    const token = viewAsToken();
    if (!token) return blockedResponse();
    if (!SAFE_METHODS.has(facts.method) && !(facts.method === 'POST' && END_PATH_RE.test(facts.url.pathname))) {
      return blockedResponse();
    }
    const headers = new Headers(init?.headers ?? (typeof input === 'object' && 'headers' in input ? (input as Request).headers : undefined));
    headers.set('Authorization', `Bearer ${token}`);
    const res = await realFetch(input, { ...init, headers, credentials: 'omit' });
    if (res.status === 401) markViewAsEnded();
    else if (res.status === 403) {
      /* The server says this view can no longer continue (flag turned off, owner removed): end it. A plain
         read-only refusal has a different code and must not end the view. */
      try {
        const body = (await res.clone().json()) as { code?: string };
        if (body?.code === 'VIEW_AS_UNAVAILABLE') markViewAsEnded();
      } catch {
        /* not JSON: nothing to read */
      }
    }
    return res;
  }) as typeof window.fetch;
}

/** Install the view-as runtime. Call ONCE, before any other app code runs (the Wedge entry imports it first).
 *  'off' = an ordinary tab, nothing changed. 'active' = a view-as tab, fully isolated. 'failed' = a handoff was
 *  presented but the isolation could not be installed: nothing is kept and the app must not mount. */
export function installViewAsRuntime(): ViewAsState {
  if (typeof window === 'undefined') return 'off';
  const w = window as ViewAsWindow;
  if (w.__WEDGE_VIEW_AS__) return w.__WEDGE_VIEW_AS__.state;

  const handoff = parseViewAsHandoff(window.location.hash);
  const existing = viewAsToken();
  if (!handoff && !existing) {
    w.__WEDGE_VIEW_AS__ = { state: 'off' };
    return 'off';
  }

  const fail = (): ViewAsState => {
    try {
      sessionStore()?.removeItem(VIEW_AS_TOKEN_KEY);
    } catch {
      /* noop */
    }
    if (handoff) {
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch {
        /* noop */
      }
    }
    w.__WEDGE_VIEW_AS__ = { state: 'failed' };
    return 'failed';
  };

  const backing = sessionStore();
  if (!backing) return fail(); // no tab-private storage: refuse rather than guess
  try {
    if (handoff) backing.setItem(VIEW_AS_TOKEN_KEY, handoff);
    const isolated = createIsolatedStorage(backing);
    const ownLs = Object.getOwnPropertyDescriptor(window, 'localStorage');
    Object.defineProperty(window, 'localStorage', { configurable: true, enumerable: true, get: () => isolated });
    undo.push(() => {
      if (ownLs) Object.defineProperty(window, 'localStorage', ownLs);
      else delete (window as unknown as Record<string, unknown>).localStorage;
    });
    if (window.localStorage !== isolated) return fail();
  } catch {
    return fail();
  }
  // Remove the fragment (and with it the token) from the address bar and history entry.
  if (handoff) {
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
      /* noop */
    }
  }
  // Swallow 'storage' events: they come from the owner's own tabs and must not reach app code here.
  const swallowStorage = (e: StorageEvent) => e.stopImmediatePropagation();
  window.addEventListener('storage', swallowStorage, true);
  undo.push(() => window.removeEventListener('storage', swallowStorage, true));
  // sendBeacon always carries cookies; this tab sends none.
  try {
    const ownBeacon = Object.getOwnPropertyDescriptor(navigator, 'sendBeacon');
    Object.defineProperty(navigator, 'sendBeacon', { configurable: true, value: () => false });
    undo.push(() => {
      if (ownBeacon) Object.defineProperty(navigator, 'sendBeacon', ownBeacon);
      else delete (navigator as unknown as Record<string, unknown>).sendBeacon;
    });
  } catch {
    /* noop */
  }
  installFetchGuard();
  w.__WEDGE_VIEW_AS__ = { state: 'active' };
  return 'active';
}

/** Test hook · forget the install so a spec can run it again. */
export function resetViewAsRuntimeForTests(): void {
  while (undo.length) {
    try {
      undo.pop()?.();
    } catch {
      /* noop */
    }
  }
  if (typeof window !== 'undefined') delete (window as ViewAsWindow).__WEDGE_VIEW_AS__;
}
